import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ticketService } from '../../services/questService';
import { getQuestStatusIndex, QUEST_STATUS } from '../../utils/questStatus';

const SERVICES_CACHE_TTL_MS = 5 * 60 * 1000;

const SERVICE_IN_USE_DELETE_MESSAGE =
  'Вид нельзя удалить, т.к есть заявки с ним. Вы можете исправить эти заявки, после чего повторить операцию.';

const MASTER_IN_USE_DELETE_MESSAGE =
  'Исполнителя нельзя удалить, т.к есть заявки с ним. Редактировать выполненные заявки не рекомендуется.';

const getCatalogDeleteError = (error, inUseMessage) => {
  const status = error.response?.status;
  if (!error.response) {
    return 'Нет соединения с сервером';
  }
  if (status === 401 || status === 403) {
    return 'Недостаточно прав для удаления';
  }
  if (status === 404) {
    return 'Запись не найдена';
  }
  return inUseMessage;
};

const normalizeServicesResponse = (payload) => {
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return items
    .map((service) => ({
      ...service,
      id: service.id ?? service.Id,
      name: service.name ?? service.Name,
      description: service.description ?? service.Description,
      price: service.price ?? service.Price,
    }))
    .filter((service) => service.id && service.name);
};

const shouldFetchServices = (state, force = false) => {
  if (force) return true;
  if (state.servicesLoading) return false;
  if (!Array.isArray(state.services) || state.services.length === 0) return true;
  if (!state.servicesLoadedAt) return true;
  return Date.now() - state.servicesLoadedAt > SERVICES_CACHE_TTL_MS;
};

// Асинхронные действия
export const fetchQuestCountsByStatus = createAsyncThunk(
  'tickets/fetchQuestCountsByStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await ticketService.getQuestCountsByStatus();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Ошибка загрузки количества заявок'
      );
    }
  }
);

export const fetchTickets = createAsyncThunk(
  'tickets/fetchTickets',
  async ({ page = 1, pageSize = 10, statusCode }, { getState, rejectWithValue }) => {
    const status = statusCode ?? getState().quests.currentTab;
    try {
      const response = await ticketService.getTickets(page, pageSize, status);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки заявок');
    }
  }
);

export const fetchTicketDetails = createAsyncThunk(
  'tickets/fetchTicketDetails',
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await ticketService.getTicket(ticketId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки деталей заявки');
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  'tickets/updateTicketStatus',
  async ({ ticketId, ticketData }, { rejectWithValue }) => {
    try {
      const response = await ticketService.updateTicket(ticketId, ticketData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления заявки');
    }
  }
);

export const createTicket = createAsyncThunk(
  'tickets/createTicket',
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await ticketService.createTicket(ticketData);
      return response;
    } catch (error) {
      const data = error.response?.data;
      const message = data?.message || data?.title || data?.errors?.join('; ') || error.message || 'Ошибка создания заявки';
      return rejectWithValue(message);
    }
  }
);

export const cancelTicket = createAsyncThunk(
  'tickets/cancelTicket',
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await ticketService.cancelTicket(ticketId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка отмены заявки');
    }
  }
);

export const fetchStatuses = createAsyncThunk(
  'tickets/fetchStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketService.getStatuses();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки статусов');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'tickets/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketService.getCategories();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки категорий');
    }
  }
);

export const fetchServices = createAsyncThunk(
  'tickets/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketService.getServices();
      return normalizeServicesResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки услуг');
    }
  },
  {
    condition: (options = {}, { getState }) =>
      shouldFetchServices(getState().quests, Boolean(options?.force)),
  }
);

export const getMasters = createAsyncThunk(
  'tickets/getMasters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketService.getMasters();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки мастеров');
    }
  }
);

export const getServices = fetchServices;

export const createService = createAsyncThunk(
  'tickets/createService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const response = await ticketService.createService(serviceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка создания услуги');
    }
  }
);

export const updateService = createAsyncThunk(
  'tickets/updateService',
  async ({ serviceId, serviceData }, { rejectWithValue }) => {
    try {
      const response = await ticketService.updateService(serviceId, serviceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления услуги');
    }
  }
);

export const deleteService = createAsyncThunk(
  'tickets/deleteService',
  async (serviceId, { rejectWithValue }) => {
    try {
      await ticketService.deleteService(serviceId);
      return serviceId;
    } catch (error) {
      return rejectWithValue(getCatalogDeleteError(error, SERVICE_IN_USE_DELETE_MESSAGE));
    }
  }
);

export const createMaster = createAsyncThunk(
  'tickets/createMaster',
  async (masterData, { rejectWithValue }) => {
    try {
      const response = await ticketService.createMaster(masterData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка создания мастера');
    }
  }
);

export const updateMaster = createAsyncThunk(
  'tickets/updateMaster',
  async ({ masterId, masterData }, { rejectWithValue }) => {
    try {
      const response = await ticketService.updateMaster(masterId, masterData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления мастера');
    }
  }
);

export const deleteMaster = createAsyncThunk(
  'tickets/deleteMaster',
  async (masterId, { rejectWithValue }) => {
    try {
      await ticketService.deleteMaster(masterId);
      return masterId;
    } catch (error) {
      return rejectWithValue(getCatalogDeleteError(error, MASTER_IN_USE_DELETE_MESSAGE));
    }
  }
);

// Начальное состояние
const initialState = {
  quests: [],
  currentQuest: null,
  statuses: [],
  categories: [],
  services: [],
  servicesLoading: false,
  servicesError: null,
  servicesLoadedAt: null,
  masters: [],
  mastersLoading: false,
  loading: false,
  loadingMore: false,
  error: null,
  currentTab: 1, // 0 - Новые, 1 - Назначены, 2 - В работе, 3 - Завершены, 4 - Отменены
  statusCounts: [0, 0, 0, 0, 0],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  },
  hasMore: true
};

// Срез для заявок
const questsSlice = createSlice({
  name: 'quests',
  initialState,
  reducers: {
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    clearCurrentQuest: (state) => {
      state.currentQuest = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    resetQuests: (state) => {
      state.quests = [];
      state.pagination.currentPage = 1;
      state.pagination.totalPages = 1;
      state.pagination.totalItems = 0;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuestCountsByStatus.fulfilled, (state, action) => {
        const counts = [0, 0, 0, 0, 0];
        const data = Array.isArray(action.payload) ? action.payload : [];
        data.forEach((item) => {
          const code =
            item.statusCode != null
              ? Number(item.statusCode)
              : getQuestStatusIndex(item.status);
          if (code >= 0 && code <= 4) {
            counts[code] = item.count ?? 0;
          }
        });
        state.statusCounts = counts;
      })
      // Обработка загрузки заявок
      .addCase(fetchTickets.pending, (state, action) => {
        const { page = 1 } = action.meta.arg || {};
        if (page === 1) {
          state.loading = true;
          state.loadingMore = false;
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        const { page = 1, statusCode } = action.meta.arg || {};
        const payload = action.payload;
        let newItems = [];
        if (payload.items && Array.isArray(payload.items)) {
          newItems = payload.items;
        } else if (payload.data && Array.isArray(payload.data)) {
          newItems = payload.data;
        } else if (Array.isArray(payload)) {
          newItems = payload;
        }

        if (page === 1) {
          state.quests = newItems;
        } else {
          // Добавляем новые заявки, избегая дубликатов по id
          const existingIds = new Set(state.quests.map(q => q.id));
          const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
          state.quests.push(...uniqueNewItems);
        }

        state.pagination = {
          currentPage: payload.page || page,
          totalPages: Math.ceil((payload.totalCount || 0) / (payload.pageSize || 10)) || 1,
          totalItems: payload.totalCount || 0,
          pageSize: payload.pageSize || 10,
        };
        state.hasMore = (payload.page || page) < state.pagination.totalPages;
        if (page === 1) {
          const tabIndex = statusCode != null ? Number(statusCode) : state.currentTab;
          if (tabIndex >= 0 && tabIndex <= 4) {
            state.statusCounts[tabIndex] = payload.totalCount ?? 0;
          }
        }
        state.loading = false;
        state.loadingMore = false;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка загрузки деталей заявки
      .addCase(fetchTicketDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuest = action.payload;
      })
      .addCase(fetchTicketDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка обновления статуса заявки
      .addCase(updateTicketStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        state.loading = false;
        // В реальном приложении здесь нужно обновить заявку в списке
        // Пока просто перезагрузим список заявок
        if (action.payload && action.payload.id) {
          const index = state.quests.findIndex(quest =>
            quest.id === action.payload.id || quest.number === action.payload.id
          );
          if (index !== -1) {
            state.quests[index] = action.payload;
          }
        }
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка отмены заявки
      .addCase(cancelTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelTicket.fulfilled, (state, action) => {
        state.loading = false;
        // Обновляем статус заявки на "Отменена"
        if (action.payload && action.payload.id) {
          const index = state.quests.findIndex(quest =>
            quest.id === action.payload.id || quest.number === action.payload.id
          );
          if (index !== -1) {
            state.quests[index] = {
              ...state.quests[index],
              status: QUEST_STATUS.CANCELLED,
            };
          }
        }
      })
      .addCase(cancelTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка создания заявки
      .addCase(createTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка загрузки статусов
      .addCase(fetchStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.statuses = action.payload;
      })
      .addCase(fetchStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка загрузки категорий
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка загрузки услуг
      .addCase(fetchServices.pending, (state) => {
        state.servicesLoading = true;
        state.servicesError = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.services = action.payload;
        state.servicesLoadedAt = Date.now();
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.servicesLoading = false;
        state.servicesError = action.payload;
        state.error = action.payload;
      })
      // Обработка загрузки мастеров
      .addCase(getMasters.pending, (state) => {
        state.mastersLoading = true;
        state.error = null;
      })
      .addCase(getMasters.fulfilled, (state, action) => {
        state.mastersLoading = false;
        state.masters = action.payload;
      })
      .addCase(getMasters.rejected, (state, action) => {
        state.mastersLoading = false;
        state.error = action.payload;
      })
      // Create service
      .addCase(createService.fulfilled, (state, action) => {
        const service = normalizeServicesResponse([action.payload])[0];
        if (service) {
          state.services.push(service);
          state.servicesLoadedAt = Date.now();
        }
      })
      // Update service
      .addCase(updateService.fulfilled, (state, action) => {
        const service = normalizeServicesResponse([action.payload])[0];
        if (service?.id) {
          const idx = state.services.findIndex(s => s.id === service.id);
          if (idx !== -1) state.services[idx] = service;
          state.servicesLoadedAt = Date.now();
        }
      })
      // Delete service
      .addCase(deleteService.fulfilled, (state, action) => {
        state.services = state.services.filter(s => s.id !== action.payload);
        state.servicesLoadedAt = Date.now();
      })
      // Create master
      .addCase(createMaster.fulfilled, (state, action) => {
        state.masters.push(action.payload);
      })
      // Update master
      .addCase(updateMaster.fulfilled, (state, action) => {
        if (action.payload?.id) {
          const idx = state.masters.findIndex(m => m.id === action.payload.id);
          if (idx !== -1) state.masters[idx] = action.payload;
        }
      })
      // Delete master
      .addCase(deleteMaster.fulfilled, (state, action) => {
        state.masters = state.masters.filter(m => m.id !== action.payload);
      });
  },
});

export const { setCurrentTab, clearCurrentQuest, clearError, setPage } = questsSlice.actions;
export default questsSlice.reducer;